import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, fmtUsd } from "@/lib/journal";
import { StackSimple } from "@phosphor-icons/react";

export const MergeDialog = ({ open, onOpenChange, portfolioId, onMerged }) => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [merging, setMerging] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const params = portfolioId === "all" ? {} : { portfolio_id: portfolioId };
    api.get("/positions/mergeable", { params })
      .then(({ data }) => setGroups(data.groups))
      .catch(() => toast.error("Failed to load mergeable positions"))
      .finally(() => setLoading(false));
  }, [open, portfolioId]);

  const merge = async () => {
    setMerging(true);
    try {
      const params = portfolioId === "all" ? {} : { portfolio_id: portfolioId };
      const { data } = await api.post("/positions/merge", null, { params });
      toast.success(`Merged ${data.merged_groups} positions, ${data.removed_trades} rows folded in`);
      onOpenChange(false);
      onMerged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Merge failed");
    } finally {
      setMerging(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="merge-dialog" className="max-h-[90vh] overflow-y-auto rounded-none border-[#1F1F1F] bg-[#0A0A0A] sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-medium tracking-tight">
            Merge Positions <span className="text-[#C58361]">/ Blend</span>
          </DialogTitle>
          <DialogDescription className="text-sm text-zinc-500">
            Repeat buys of the same asset become one open position at a blended average cost.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          {loading ? (
            <p className="font-mono text-sm text-zinc-600">Scanning open positions…</p>
          ) : groups.length === 0 ? (
            <div className="border border-dashed border-[#1F1F1F] px-4 py-12 text-center" data-testid="merge-empty-state">
              <StackSimple size={20} className="mx-auto text-[#C58361]" weight="light" />
              <p className="mt-4 text-sm text-zinc-500">Nothing to merge — no duplicate open positions.</p>
            </div>
          ) : (
            <div className="divide-y divide-[#1F1F1F] border border-[#1F1F1F]">
              {groups.map((g) => (
                <div key={`${g.portfolio_id}-${g.asset}-${g.network}`}
                     className="flex items-center justify-between px-4 py-3"
                     data-testid={`merge-group-${g.asset}`}>
                  <div>
                    <p className="font-mono text-sm text-white">
                      {g.asset} <span className="ml-2 text-xs text-zinc-500">{g.trades} buys</span>
                    </p>
                    <p className="mt-0.5 font-mono text-[10px] text-zinc-600">
                      {g.portfolio} · {g.network}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-sm text-zinc-300">{g.volume}</p>
                    <p className="font-mono text-[10px] text-zinc-600">
                      avg {fmtUsd(g.avg_price, 4)} · {fmtUsd(g.cost_basis)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          <Button
            data-testid="merge-confirm-btn"
            onClick={merge}
            disabled={merging || groups.length === 0}
            className="w-full rounded-none bg-[#C58361] py-6 font-medium text-black hover:bg-[#A86B4C] disabled:opacity-40"
          >
            {merging ? "Merging…" : `Merge ${groups.length || ""} Position${groups.length === 1 ? "" : "s"}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
